import { API_URL } from "../config";

const DEFAULT_TIMEOUT = 60000 * 5; // 5min

export type ApiClientOptions = {
  baseUrl?: string;
  apiKey?: string;
  timeoutMs?: number;
};

export class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

export function createClient(opts: ApiClientOptions = {}) {
  const baseUrl =
    opts.baseUrl ??
    API_URL;

  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT;

  async function request<T = unknown>(
    path: string,
    init: RequestInit & { responseType?: "json" | "blob" } = {},
  ): Promise<T> {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);

    const isFormData =
      typeof FormData !== "undefined" && init.body instanceof FormData;

    const baseHeaders: Record<string, string> = {
      ...(opts.apiKey ? { "x-api-key": opts.apiKey } : {}),
    };

    if (!isFormData) {
      const incoming = init.headers as Record<string, string> | undefined;
      const hasCT =
        !!incoming &&
        Object.keys(incoming).some((k) => k.toLowerCase() === "content-type");

      if (!hasCT) baseHeaders["Content-Type"] = "application/json";
    }

    const headers: HeadersInit = { ...baseHeaders, ...(init.headers || {}) };

    try {
      const res = await fetch(`${baseUrl}${path}`, {
        ...init,
        headers,
        signal: ctrl.signal,
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        const ct = res.headers.get("content-type") || "";

        try {
          if (ct.includes("application/json")) {
            const data = await res.json();
            msg = data?.error || data?.message || msg;
          } else {
            const text = await res.text();
            if (text) msg = text;
          }
        } catch {
          // ignore parse errors
        }

        throw new ApiError(msg, res.status);
      }

      if (init.responseType === "blob") {
        return (await res.blob()) as unknown as T;
      }

      return (await res.json()) as T;
    } catch (e: any) {
      if (e?.name === "AbortError") throw new ApiError("Request timeout", 408);
      throw e;
    } finally {
      clearTimeout(t);
    }
  }

  return { request };
}

const api = createClient();

export default api;
